import type { Theme } from '@material-ui/core'
import { Card, makeStyles } from '@material-ui/core'
import { formatName, mapEventsToMapMarkers, translateEventType } from '@paintscout/util/builder'
import { GoogleMap, OverlayView, OverlayViewF } from '@react-google-maps/api'
import moment from 'moment'
import type { CalendarEvent, CalendarMapMarker } from 'paintscout'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { MapMarker } from '@paintscout/ui/icons'
import { useGoogleMaps } from '@paintscout/ui/providers/GoogleMapsProvider'
import { SelectableChipSmall } from '@paintscout/ui/components/SelectableChip'
import Spinner from '@paintscout/ui/components/Spinner'
import Typography from '@paintscout/ui/components/Typography'
import Tooltip from '@paintscout/ui/components/Tooltip'
import { useClientOptions } from '../../providers/ClientOptionsProvider'

const useStyles = makeStyles<Theme, EventsMapProps>((theme) => ({
  root: {
    position: 'relative',
    borderRadius: 0,
    borderBottomLeftRadius: ({ framed }) => (framed ? theme.borderRadius.md : 0),
    borderBottomRightRadius: ({ framed }) => (framed ? theme.borderRadius.md : 0),
    border: ({ framed }) => (framed ? `1px solid ${theme.paintscout.palette.brand.Neutral.P90}` : 'none'),
    boxShadow: 'none',
    overflow: 'hidden',
    '& .gm-fullscreen-control': {
      position: 'absolute !important',
      top: '21px !important',
      right: '25px !important',
      margin: '0 !important',
      borderRadius: `${theme.borderRadius.md} !important`,
      boxShadow: `${theme.shadows[1]} !important`,
      width: '44px !important',
      height: '44px !important'
    }
  },
  buttonContainer: {
    position: 'absolute',
    top: 21,
    left: 16,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing(2),
    '& div': {
      height: '28.5px'
    }
  },
  mapMarker: {
    position: 'absolute',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: ({ onMarkerClick }) => (onMarkerClick ? 'translate(-50%, -100%) scale(1.2)' : undefined),
      transition: 'transform 0.2s ease-in-out',
      zIndex: 1000
    }
  },
  tooltip: {
    backgroundColor: theme.paintscout.palette.brand.Neutral.P35,
    borderRadius: theme.borderRadius.md,
    opacity: 1,
    color: theme.palette.common.white,
    maxWidth: 350,
    width: 'max-content',
    padding: theme.spacing(1.5, 2)
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column'
  },
  tooltipRow: {
    marginBottom: theme.spacing(1),
    '& span': {
      marginRight: theme.spacing(1)
    },
    '&:last-child': {
      marginBottom: 0
    }
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.6)',
    zIndex: 2
  }
}))

export interface EventsMapProps {
  events: CalendarEvent[]
  onMarkerClick?: (event: CalendarEvent, e?: React.MouseEvent<HTMLElement>) => void
  maxZoom?: number
  dayView?: boolean
  toolbarComponent?: React.ReactNode
  editingEventId?: string
  hideMapTypeButtons?: boolean
  disableTooltip?: boolean
  loading?: boolean
  framed?: boolean
}

type TooltipPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'

export function EventsMap(props: EventsMapProps) {
  const {
    events,
    onMarkerClick,
    maxZoom = 12,
    toolbarComponent,
    dayView,
    editingEventId,
    hideMapTypeButtons,
    disableTooltip,
    loading
  } = props
  const classes = useStyles(props)
  const { isLoaded: gmapsIsLoaded } = useGoogleMaps()

  const [markers, setMarkers] = useState<CalendarMapMarker[]>([])
  const [mapType, setMapType] = useState<'roadmap' | 'satellite'>('roadmap')
  const [isLoaded, setIsLoaded] = useState(false)
  const [tooltipOpenIndex, setTooltipOpenIndex] = useState<number | null>(null)
  const [tooltipPlacement, setTooltipPlacement] = useState<TooltipPlacement>('bottom-start')

  const { options } = useClientOptions()

  const mapRef = useRef<any>()
  const prevPositionsRef = useRef<string>('')

  const onMapLoad = useCallback((map) => {
    mapRef.current = map
    setIsLoaded(gmapsIsLoaded)
  }, [])

  useEffect(() => {
    if (isLoaded) {
      const currentMapRef = mapRef?.current as any

      const markerData = mapEventsToMapMarkers({ events, options, editingEventId })
      setMarkers(markerData)

      // Only re-fit bounds when the actual lat/lng positions change, not on every render
      const positionKey = markerData
        .map(({ lat, lng }) => `${lat},${lng}`)
        .sort()
        .join('|')
      if (positionKey === prevPositionsRef.current) return
      prevPositionsRef.current = positionKey

      const bounds = new google.maps.LatLngBounds()
      if (markerData.length) {
        markerData.forEach(({ lat, lng }) => {
          bounds.extend({ lat, lng })
        })
      } else {
        const [lng, lat] = options.options?.companyCoordinates ?? [0, 0]
        bounds.extend({ lat, lng })
      }

      const boundsListener = currentMapRef.addListener('bounds_changed', () => {
        const currentZoom = currentMapRef.getZoom()
        if (currentZoom > maxZoom) {
          currentMapRef.setZoom(maxZoom)
        }
        google.maps.event.removeListener(boundsListener)
      })

      currentMapRef.fitBounds(bounds, { maxZoom, padding: 50 })
    }
  }, [isLoaded, events, editingEventId])

  const handleMarkerClick = (e: React.MouseEvent<HTMLElement>, event: CalendarEvent, index: number) => {
    e.stopPropagation()
    setTooltipOpenIndex(index)
    if (onMarkerClick) {
      onMarkerClick?.(event, e)
      setTooltipOpenIndex(null)
    }
  }

  const handleMarkerMouseEnter = (index: number, e: React.MouseEvent<HTMLDivElement>) => {
    // Skip placement updates when the tooltip is disabled. The Tooltip is
    // keyed by tooltipPlacement, so changing it remounts the marker wrapper
    // — on touch the synthesized mouseenter fires before click, and the
    // remount swaps the DOM node out from under the pending click before it
    // reaches handleMarkerClick.
    if (disableTooltip) return
    const map = mapRef.current
    if (map) {
      const mapDiv = map.getDiv()
      const markerRect = e.currentTarget.getBoundingClientRect()
      const mapRect = mapDiv.getBoundingClientRect()
      const tooltipWidth = 370
      const tooltipHeight = 250

      const nearBottom = markerRect.bottom + tooltipHeight > mapRect.bottom
      const nearRight = markerRect.left + tooltipWidth > mapRect.right

      const vertical = nearBottom ? 'top' : 'bottom'
      const horizontal = nearRight ? 'end' : 'start'
      setTooltipPlacement(`${vertical}-${horizontal}` as TooltipPlacement)
    }
    setTooltipOpenIndex(index)
  }

  const getTooltipContent = (event: CalendarEvent) => {
    const isMultiDay = !moment(event.start).isSame(moment(event.end), 'day')
    const formattedTime =
      !event.start || !event.end
        ? ''
        : `${moment(event.start).format(dayView && !isMultiDay ? 'h:mma' : 'MMM D, h:mma')} - ${moment(event.end).format(isMultiDay ? 'MMM D, h:mma' : 'h:mma')}`
    const contactLine = event.contact?.firstName ? formatName(event.contact) : ''
    return (
      <div className={classes.tooltipContent}>
        {event?.eventType && (
          <Typography weight={700} className={classes.tooltipRow}>
            {translateEventType({ eventType: event.eventType, options })}{' '}
            {event.estimator?.name && ` for ${event.estimator.name}`}
            {event.id === editingEventId ? ' (current booking)' : ''}
          </Typography>
        )}
        {formattedTime && (
          <Typography className={classes.tooltipRow}>
            <span className="fa-solid fa-calendar"></span>
            {formattedTime}
          </Typography>
        )}
        {contactLine && (
          <Typography className={classes.tooltipRow}>
            <span className="fa-solid fa-user" />
            {contactLine}
          </Typography>
        )}
        {event?.address && (
          <Typography className={classes.tooltipRow}>
            <span className="fa-solid fa-location-dot" style={{ marginLeft: 1 }} />
            {event.address}
          </Typography>
        )}
      </div>
    )
  }

  return (
    <Card className={classes.root}>
      {toolbarComponent}
      {loading && (
        <div className={classes.loadingOverlay}>
          <Spinner size={40} />
        </div>
      )}
      <GoogleMap
        options={{ disableDefaultUI: true }}
        mapContainerStyle={{
          width: '100%',
          height: '800px'
        }}
        onLoad={onMapLoad}
        mapTypeId={mapType}
      >
        {!hideMapTypeButtons && (
          <Card className={classes.buttonContainer}>
            <SelectableChipSmall
              label="Map"
              selected={mapType === 'roadmap'}
              variant="filled"
              onClick={() => setMapType('roadmap')}
            />
            <SelectableChipSmall
              label="Satellite"
              selected={mapType === 'satellite'}
              variant="filled"
              onClick={() => setMapType('satellite')}
            />
          </Card>
        )}

        {markers.map(({ lat, lng, color, eventNumber, event, overlapIndex }, index) => (
          <OverlayViewF key={index} position={{ lat, lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
            <Tooltip
              mapTooltip
              key={tooltipPlacement}
              classes={{ tooltip: classes.tooltip }}
              content={getTooltipContent(event)}
              hideIcon
              placement={tooltipPlacement}
              open={tooltipOpenIndex === index && !disableTooltip}
              onOpen={() => setTooltipOpenIndex(index)}
              onClose={() => setTooltipOpenIndex(null)}
              PopperProps={{
                disablePortal: true
              }}
            >
              <div
                className={classes.mapMarker}
                onClick={(e) => handleMarkerClick(e, event, index)}
                onMouseEnter={(e) => handleMarkerMouseEnter(index, e)}
                onMouseLeave={() => setTooltipOpenIndex(null)}
                style={{
                  transform: `translate(-50%, -100%) translateX(${overlapIndex * 8}px)`
                }}
              >
                <MapMarker color={color} eventNumber={dayView ? eventNumber : null} />
              </div>
            </Tooltip>
          </OverlayViewF>
        ))}
      </GoogleMap>
    </Card>
  )
}
